/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console */

import React from 'react';
import PropTypes from 'prop-types';
import Screen from '../Screen';
import Groupview from '../../Groupview/Groupview';
import Fab from '../../Fab/Fab';
import Button from '../../Button/Button';
import Dialog from '../../Dialog/Dialog';
import Service from '../../Util/Service';
import $ from '../../Util/Util';
import AddIcon from './baseline_add_white_24dp.png';
import './Groups.css';

/**
 * Represents the group maintenance screen.
 */

export default class Groups extends Screen {

    /**
     * Properties.
     */

    static propTypes = {
        onGroupSelected:              PropTypes.func.isRequired
    }

    /**
     * Default state.
     */

    state = {
        groups:     [],
        group:      null
    }

    /**
     * Loads the groups.
     */

    _refresh = () => {

        // Showing the progress indicator

        this.props.onLoading(true);

        // Loading the groups

        Service.loadGroup().then((groups) => {

            // Storing the groups

            this.setState({ groups: groups });

            // Hiding the progress indicator

            this.props.onLoading(false);
        }).catch((error) => {

            // Hiding the progress indicator

            this.props.onLoading(false);

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
        });
    }

    /**
     * Events that fires when the user selects a group from the list.
     * 
     * @param   {Object}    group       selected group
     */

    _groupSelected = (group) => {

        // Do nothing if there is no ID of the selected group

        if (this.state.group && !this.state.group.id ) {
            return;
        }

        // Storing the selected group

        if (this.props.context.environment.browser.info.mobile) {
            this.props.onGroupSelected(group);
        } else {
            this.setState({ group: (this.state.group && this.state.group.id === group.id) ? null : group }, () => {
                if (this.groupview) {
                    this.groupview.load(this.state.group);
                }
            });
        }
    }

    /**
     * Adds a new group.
     */

    _addNewGroup = () => {

        // Create the group

        let group = { name: this.props.context.locale.group.newgroup, members: [], defaultaccess: 'rw' };

        // Storing the group

        if (this.props.context.environment.browser.info.mobile) {
            this.props.onGroupSelected(group);
        } else {
            this.setState({ group: group }, () => {
                this.groupview.load(this.state.group);
            });
        }
    }

    /**
     * Event that fires when the user closes the group maintenance.
     * 
     * @param   {Boolean}   refresh     refreshing the groups
     */    

    _onCloseMaintenance = (refresh) => {

        // Clearing the groups

        this.setState( { group: null });

        // Refreshing the groups

        if (refresh) {
            this._refresh();
        }
    }

    /**
     * Event that fires when the user wants to delete the selected group.
     */

    _onDeleteGroup = () => {
        this.deleteConfirmDialog.open();
    }

    /**
     * Event that fires when the user wants to cancel the deletion of the
     * selected group.
     */

    _cancelDelete = () => {
        this.deleteConfirmDialog.close();
    }

    /**
     * Deletes the selected group.
     */

    _delete = () => {

        // Closing confirm dialog

        this.deleteConfirmDialog.close().then(() => {

            // Showing progress indicator

            this.props.onLoading(true);

            // Deleting the group

            Service.deleteGroup(this.state.group.id).then(() => {

                // Hiding progress indicator

                this.props.onLoading(false);

                // Closing maintenance mode

                this.setState({ group: null }, () => {

                    // Showing confirm

                    $.fireEvent('snackbar', { message: this.props.context.locale.group.deletedmessage });

                    // Refreshing

                    this._refresh();
                });
            }).catch((error) => {

                // Hiding progress indicator

                this.props.onLoading(false);

                // Showing error

                $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
            });
        });
    }

    /** 
     * Event that fires when the component mounted.
     */

    componentDidMount() {

        // Setting actionbar title

        this.props.onSetActionbarTitle(this.props.context.locale.group.title);

        // Refreshing the groups

        this._refresh();
    }

    /** 
     * Group maintenance screen renderer.
     */

    render() {
        return (
            <div className="groups">

                {/* Groups container */}

                <div className="groups-container horizontal">

                    {/* List of groups */}

                    <div className="groups-list vertical" style={{ height: (this.props.context.environment.screen.usefullheight - 32) + 'px' }}>
                        { this.state.groups.map((item, index) =>
                            <div key={index} className={`groups-group ${(this.state.group && this.state.group.id === item.id) ? 'groups-group-selected' : 'groups-group-unselected'}`} onClick={this._groupSelected.bind(this, item)}>{item.name}</div>
                        )}

                        {/* Add new group */}

                        { !this.state.group && <div className="groups-fab">
                            <Fab icon={AddIcon} tooltip={this.props.context.locale.group.newgrouptooltip} onTouch={this._addNewGroup} />
                        </div> }
                    </div>

                    {/* Maintenance */}

                    { !this.props.context.environment.browser.info.mobile && this.state.group && <div className="groups-maintenance">
                        <Groupview ref={c => {this.groupview = c;}} context={this.props.context} onLoading={this.props.onLoading} onClose={this._onCloseMaintenance} onDelete={this._onDeleteGroup} />
                    </div> }
                </div>

                {/* Delete confirm dialog */}

                <Dialog ref={c => {this.deleteConfirmDialog = c;}} width={360} height={104} closetooltip={this.props.context.locale.group.deletedialog.close}>

                    {/* Message */}

                    <div className="groups-deletedialog-title">{this.props.context.locale.group.deletedialog.message}</div>
                    <div className="groups-deletedialog-groupname">{this.state.group ? this.state.group.name : ''}</div>

                    {/* Buttons */}

                    <div className="horizontal">

                        {/* Spacer */}

                        <div className="flex"></div>
                        
                        {/* Delete */}

                        <Button label={this.props.context.locale.group.deletedialog.buttons.yes.label} tooltip={this.props.context.locale.group.deletedialog.buttons.yes.tooltip} width={60} onTouch={this._delete} />

                        {/* Cancel */}

                        <Button label={this.props.context.locale.group.deletedialog.buttons.cancel.label} tooltip={this.props.context.locale.group.deletedialog.buttons.cancel.tooltip} width={60} leftgap={8} onTouch={this._cancelDelete} />
                    </div>
                </Dialog>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Screens/Groups/Groups.js